import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FaqAccordionProps {
    items: Faq[]
    defaultOpen?: number | null
}

export const FaqAccordion: FC<FaqAccordionProps> = ({ items, defaultOpen = 0 }): ReactNode => {
    const [open, setOpen] = useState<number | null>(defaultOpen)
    const baseId = useId()

    return (
        <div className='border-t border-ink/10'>
            {items.map((item, index) => {
                const expanded = open === index
                const buttonId = `${baseId}-q-${index}`
                const panelId = `${baseId}-a-${index}`

                return (
                    <div key={item.question} className='border-b border-ink/10'>
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={expanded}
                                aria-controls={panelId}
                                onClick={() => setOpen(expanded ? null : index)}
                                className='group flex w-full items-center justify-between gap-6 py-6 text-left'
                            >
                                <span className='font-heading text-[1.2rem] font-semibold leading-snug tracking-[-0.025em] text-ink transition-colors group-hover:text-ember sm:text-[1.4rem]'>
                                    {item.question}
                                </span>

                                <span
                                    className={cn(
                                        'flex size-10 shrink-0 items-center justify-center rounded-full border transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-500 ease-expo',
                                        expanded ? 'rotate-45 border-ink bg-ink text-cream' : 'border-ink/15 text-ink group-hover:border-ink/40'
                                    )}
                                    aria-hidden='true'
                                >
                                    <Plus className='size-4' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            className={cn(
                                'grid transition-[grid-template-rows,opacity] duration-500 ease-expo',
                                expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                            )}
                        >
                            <div className='overflow-hidden'>
                                <p className='max-w-2xl pb-7 pr-14 text-[16px] leading-relaxed text-slate'>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
